import { getProfile } from "../services/mortgageDetails";
import { LoadingCircle } from "../components/Common/loading";
import { useUserMortgageProfileStore } from "../stores/userProfileStore";
import {
  type ReactNode,
  createContext,
  useContext,
  useEffect,
  useState,
} from "react";

interface AuthContextProps {
  authenticated: boolean;
  setAuthenticated: (authenticated: boolean) => void;
}

interface ErrorWithResponse extends Error {
  response?: {
    status: number;
  };
}

const AuthContext = createContext<AuthContextProps | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [authenticated, setAuthenticated] = useState<boolean>(false);
  const { setUserMortgageProfile } = useUserMortgageProfileStore();

  useEffect(() => {
    const checkAuth = async () => {
      try {
        setLoading(true);
        const accountData = await getProfile();
        setUserMortgageProfile(accountData);

        if (accountData) {
          setAuthenticated(true);
        } else {
          setAuthenticated(false);
        }

        setLoading(false);
      } catch (error) {
        const err = error as ErrorWithResponse;
        setAuthenticated(false);

        if (err.response && err.response.status === 401) {
          setAuthenticated(false);
        } else {
          console.error("Failed to check authentication", error);
        }
        setLoading(false);
      }
    };

    checkAuth();
  }, [setUserMortgageProfile]);

  return (
    <AuthContext.Provider
      value={{
        authenticated,
        setAuthenticated,
      }}
    >
      {loading ? (
        <div className="absolute w-full h-full flex items-center justify-center bg-[#F9FBFF]">
          <LoadingCircle />
        </div>
      ) : (
        children
      )}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextProps => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
